import Chainable from "./Chainable.mts";
import UArray from "./UArray.mts";
import UFunction from "./UFunction.mts";
import UNumber from "./UNumber.mts";

namespace Compare {
  export type Order = keyof typeof order;
  export type Orderable = string | number | Date | boolean | null | undefined;

  export type Selector<T, R = unknown> = (subject: T) => R;
  export namespace Orderable {
    export type Selector<T, R extends Orderable = Orderable> =
      | UArray.Maybe<
          (subject: T) => UArray.Maybe<R>
          // May be useful in the future, must add impelmention if so
          // | [Comparable, Order][]
        >
      | [(subject: T) => UArray.Maybe<R>, Order][];
    export namespace Selector {
      export type CanReturnNullish<S extends Selector<any>> =
        S extends UFunction.Any
          ? UFunction.CanReturnNullish<S>
          : S extends readonly (infer Item)[]
            ? Item extends UFunction.Any
              ? UFunction.CanReturnNullish<Item>
              : Item extends [infer Fn extends UFunction.Any, Order]
                ? UFunction.CanReturnNullish<Fn>
                : never
            : never;
    }
  }

  export const order = {
    asc: Chainable.create(
      Chainable.wrapFactory(
        ({ nullsRank = "low" as "low" | "high" }) =>
          <A extends Orderable, B extends Orderable>(
            a: A,
            b: B
          ):
            | UNumber.Sign
            | (A extends undefined
                ? B extends undefined
                  ? undefined
                  : never
                : never) => {
            const nullSign = nullsRank === "high" ? -1 : 1;
            // @ts-expect-error
            if (a == null) return b == null ? undefined : -1 * nullSign;
            if (b == null) return (1 * nullSign) as UNumber.Sign;
            // @ts-expect-error
            return a > b ? 1 : a < b ? -1 : 0;
          }
      ),
      {
        nulls: () => (rank: "low" | "high") => ({ nullsRank: rank }),
      }
    ),
    desc: Chainable.create(
      Chainable.wrapFactory(
        ({ nullsRank = "low" as "low" | "high" }) =>
          <A extends Orderable, B extends Orderable>(
            a: A,
            b: B
          ):
            | UNumber.Sign
            | (A extends undefined
                ? B extends undefined
                  ? undefined
                  : never
                : never) => {
            const asc = order.asc.nulls(nullsRank)(a, b);
            return asc ? (-asc as Comparator.Result) : asc;
          }
      ),
      {
        nulls: () => (rank: "low" | "high") => ({ nullsRank: rank }),
      }
    ),
  };

  export const by = <T,>(
    a: T,
    b: T,
    selector: Orderable.Selector<T>
  ): Comparator.Result => {
    return Comparator.nullable(selector)(a, b) ?? 0;
  };

  export type Comparator<T> = (a: T, b: T) => Comparator.Result;
  export namespace Comparator {
    export type Result = UNumber.Sign;
    export type Nullable<T> = (a: T, b: T) => Nullable.Result;
    export namespace Nullable {
      export type Result = UNumber.Sign | undefined;
      export type Options = { nulls?: "low" | "high" };
    }

    // This is for when we want to support filtering in a function that uses
    // comparison. For example, in `maxIndexBy`, .filter is not sufficient since
    // it will result in the wrong index
    export function nullable<T>(
      selector: Orderable.Selector<T>,
      options?: Nullable.Options
    ): Nullable<T>;
    export function nullable<T>(
      comparator: Nullable<T>,
      options?: Nullable.Options
    ): Nullable<T>;
    export function nullable<T>(
      selectorOrComparator: Orderable.Selector<T> | Nullable<T>,
      options?: Nullable.Options
    ): Nullable<T>;
    export function nullable<T>(
      selectorOrComparator: Orderable.Selector<T> | Nullable<T>,
      { nulls = "low" as "low" | "high" }: Nullable.Options = {}
    ): Nullable<T> {
      if (
        typeof selectorOrComparator === "function" &&
        UFunction.hasArgCountOf(selectorOrComparator, 2)
      )
        return selectorOrComparator;

      return (a, b) => {
        let hasZero = false;
        for (const layer of UArray.ensure(selectorOrComparator)) {
          const [selector, order] =
            layer instanceof Array ? layer : ([layer, "asc"] as const);

          const resA = UArray.ensure(selector(a));
          const resB = UArray.ensure(selector(b));

          for (let i = 0; i < resA.length; ++i) {
            const res = Compare.order[order as Order].nulls(nulls)(
              resA[i],
              resB[i]
            );
            if (res) return res;
            if (res === 0) hasZero = true;
          }
        }
        return hasZero ? 0 : undefined;
      };
    }
  }
  export function comparator<T>(selector: Orderable.Selector<T>): Comparator<T>;
  export function comparator<T>(comparator: Comparator<T>): Comparator<T>;
  export function comparator<T>(
    selectorOrComparator: Orderable.Selector<T> | Comparator<T>
  ): Comparator<T>;
  export function comparator<T>(
    selectorOrComparator: Orderable.Selector<T> | Comparator<T>
  ): Comparator<T> {
    if (
      typeof selectorOrComparator === "function" &&
      UFunction.hasArgCountOf(selectorOrComparator, 2)
    )
      return selectorOrComparator;

    return (a, b) => by(a, b, selectorOrComparator);
  }

  export type Equator<T> = (a: T, b: T) => boolean;
  export function equator<T>(selector: Selector<T>): Equator<T>;
  export function equator<T>(equator: Equator<T>): Equator<T>;
  export function equator<T>(
    selectorOrEquator: Selector<T> | Equator<T>
  ): Equator<T>;
  export function equator<T>(
    selectorOrEquator: Selector<T> | Equator<T>
  ): Equator<T> {
    if (UFunction.hasArgCountOf(selectorOrEquator, 2)) return selectorOrEquator;

    return (a, b) => selectorOrEquator(a) === selectorOrEquator(b);
  }
}

export default Compare;
